import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { openAuthenticatedBillingPortal } from "@/lib/accountApi";

interface OverdueDailyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Dias em atraso (só pra texto; opcional). */
  diasAtrasado?: number | null;
}

/**
 * OverdueDailyDialog — pop-up de aviso enquanto a assinatura está atrasada mas
 * ainda não bloqueada (dias 0-6). O AppLayout controla pra aparecer no máximo
 * 1x por dia (overdueDailyPopup). Dismissível ("Depois").
 */
export const OverdueDailyDialog = ({
  open,
  onOpenChange,
}: OverdueDailyDialogProps) => {
  const [openingPortal, setOpeningPortal] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);

  const regularizar = async () => {
    setOpeningPortal(true);
    setPortalError(null);
    try {
      await openAuthenticatedBillingPortal();
      onOpenChange(false);
    } catch (error) {
      setPortalError(error instanceof Error ? error.message : "Não foi possível abrir o Stripe agora.");
    } finally {
      setOpeningPortal(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-xs rounded-xl">
        <DialogHeader className="items-center text-center sm:text-center">
          <div
            data-testid="overdue-attention-icon"
            aria-hidden="true"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-destructive/20 bg-destructive/10 text-destructive"
          >
            <AlertTriangle className="h-5 w-5" />
          </div>
          <DialogTitle className="whitespace-nowrap text-sm">
            Não deixe seus resultados para trás
          </DialogTitle>
          <DialogDescription className="text-center text-xs">
            Atualize seu pagamento para manter sua dieta e continuar avançando.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-1 flex flex-col gap-2">
          <button
            type="button"
            data-testid="overdue-regularizar"
            onClick={() => void regularizar()}
            disabled={openingPortal}
            className="w-full rounded-lg bg-foreground py-2.5 text-sm font-semibold text-background transition-all active:scale-[0.98] active:opacity-90"
          >
            {openingPortal ? "Abrindo Stripe…" : "Atualizar pagamento"}
          </button>
          {portalError && (
            <p role="alert" className="text-center text-xs text-muted-foreground">
              {portalError}
            </p>
          )}
          <button
            type="button"
            data-testid="overdue-depois"
            onClick={() => onOpenChange(false)}
            className="w-full py-2 text-xs text-muted-foreground"
          >
            Continuar no app
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

OverdueDailyDialog.displayName = "OverdueDailyDialog";
